import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { CardSkeleton } from '../components/LoadingSkeleton';

const DiscoverLists = () => {
    const { isAuthenticated } = useContext(AuthContext);
    const navigate = useNavigate();
    const [lists, setLists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('newest'); // 'newest' or 'popular'
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        setPage(1);
        fetchLists(1);
    }, [sortBy]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setPage(1);
            fetchLists(1);
        }, 500);
        return () => clearTimeout(timeout);
    }, [searchTerm]);

    useEffect(() => {
        fetchLists(page);
    }, [page]);

    const fetchLists = async (currentPage = page) => {
        try {
            setLoading(true);
            const res = await axios.get('/api/game-lists', {
                params: {
                    search: searchTerm,
                    sort: sortBy,
                    page: currentPage
                }
            });
            setLists(res.data.data);
            setTotalPages(res.data.totalPages);
            setError('');
        } catch (err) {
            console.error(err);
            setError('Failed to load lists');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h1 style={{ color: 'var(--text-primary)' }}>Discover Lists</h1>
                {isAuthenticated && (
                    <Link to="/create-list" style={{ 
                        background: 'var(--primary-color)', 
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '6px', 
                        textDecoration: 'none',
                        fontSize: '0.9rem',
                        whiteSpace: 'nowrap',
                        width: 'auto'
                    }}>
                        + Create List
                    </Link>
                )}
            </div>

            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <input
                    type="text"
                    placeholder="Search lists..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--card-bg)',
                        color: 'var(--text-primary)',
                        flex: 1,
                        minWidth: '200px'
                    }}
                />
                <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={{
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--card-bg)',
                        color: 'var(--text-primary)'
                    }}
                >
                    <option value="newest">Newest</option>
                    <option value="popular">Most Popular</option>
                </select>
            </div>

            {error && <div style={{ color: 'var(--error-text)', marginBottom: '20px' }}>{error}</div>}

            {loading ? (
                <CardSkeleton count={6} />
            ) : lists.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                    No lists found. Be the first to create one!
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '15px' }}>
                    {lists.map(list => (
                        <div key={list._id} style={{ 
                            background: 'var(--card-bg)', 
                            borderRadius: '12px', 
                            overflow: 'hidden',
                            border: '1px solid var(--border-color)',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            cursor: 'pointer',
                            padding: '15px'
                        }} 
                        onClick={() => navigate(`/lists/${list._id}`)}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}>
                            <h3 style={{ 
                                margin: '0 0 10px 0', 
                                color: 'var(--text-primary)',
                                fontSize: '1.1rem',
                                fontWeight: '600',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>{list.title}</h3>
                            <p style={{ 
                                color: 'var(--text-secondary)', 
                                fontSize: '0.9rem', 
                                marginBottom: '15px', 
                                minHeight: '40px',
                                lineHeight: '1.4',
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                display: '-webkit-box', 
                                WebkitLineClamp: 2, 
                                WebkitBoxOrient: 'vertical' 
                            }}>
                                {list.description || 'No description'}
                            </p>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                paddingTop: '10px',
                                borderTop: '1px solid var(--border-color)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    {list.owner.profile?.avatar?.url ? (
                                        <img src={list.owner.profile.avatar.url} alt={list.owner.username} style={{ width: '20px', height: '20px', borderRadius: '50%' }} />
                                    ) : (
                                        <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#555' }}></div>
                                    )}
                                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{list.owner.username}</span>
                                </div>
                                <div style={{ display: 'flex', gap: '12px', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                                    <span>🎮 {list.games.length}</span>
                                    <span>❤️ {list.likeCount}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '30px', gap: '10px' }}>
                    <button 
                        disabled={page === 1} 
                        onClick={() => setPage(p => p - 1)}
                        style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: page === 1 ? 'var(--bg-secondary)' : 'var(--primary-color)', color: page === 1 ? 'var(--text-secondary)' : 'white', cursor: page === 1 ? 'default' : 'pointer' }}
                    >
                        Previous
                    </button>
                    <span style={{ display: 'flex', alignItems: 'center', color: 'var(--text-primary)' }}>
                        Page {page} of {totalPages}
                    </span>
                    <button 
                        disabled={page === totalPages} 
                        onClick={() => setPage(p => p + 1)}
                        style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: page === totalPages ? 'var(--bg-secondary)' : 'var(--primary-color)', color: page === totalPages ? 'var(--text-secondary)' : 'white', cursor: page === totalPages ? 'default' : 'pointer' }}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default DiscoverLists;
